'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
}

export default function BookingConfirmation({ booking }: Props) {
  const refNumber = `MGC-${booking.selectedDate?.replace(/-/g, '').slice(2)}-${booking.selectedTime?.replace(':', '')}`;
  const displayDate = booking.selectedDate
    ? new Date(booking.selectedDate + 'T12:00:00').toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const notifiedRef = useRef(false);

  useEffect(() => {
    // Only fire once per confirmation render to avoid duplicate notifications
    if (notifiedRef.current) return;
    notifiedRef.current = true;

    const cartLabel = booking.cart === 'none' || !booking.cart ? 'Walking' : `${booking.cart} cart`;
    const visitorName = (booking.visitorData?.['full_name'] as string) ||
      (booking.visitorData?.['name'] as string) || 'Guest';
    const visitorEmail = (booking.visitorData?.['email'] as string) || '';

    fetch('/api/bookings/notify-staff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitorName,
        visitorEmail,
        bookingRef: refNumber,
        date: displayDate,
        teeTime: booking.selectedTime || '',
        players: booking.players,
        holes: booking.holes,
        cart: cartLabel,
        paymentMethod: booking.paymentMethod || 'On-site',
        total: String(booking.total),
        paymentStatus: booking.paymentMethod === 'payfast' ? 'COMPLETE' : 'pending',
      }),
    }).catch(() => {
      // Non-blocking — silently ignore errors
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-2xl mx-auto text-center py-10 fade-in">
      <div className="w-20 h-20 rounded-full bg-green-light flex items-center justify-center mx-auto mb-6">
        <Icon name="CheckCircleIcon" size={40} variant="outline" className="text-secondary" />
      </div>
      <h2 className="text-3xl font-extrabold text-foreground mb-2">Booking Confirmed!</h2>
      <p className="text-muted-foreground text-base mb-6">Your tee time has been secured. A confirmation email has been sent to your registered address.</p>

      <div className="bg-card border border-border rounded-2xl p-6 shadow-card text-left mb-6">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Booking Reference</div>
            <div className="text-xl font-bold text-primary font-tabular">{refNumber}</div>
          </div>
          <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center">
            <span className="text-2xl">⛳</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            { label: 'Date', value: displayDate },
            { label: 'Tee Time', value: booking.selectedTime },
            { label: 'Holes', value: `${booking.holes} Holes` },
            { label: 'Players', value: String(booking.players) },
            { label: 'Cart', value: booking.cart === 'none' ? 'Walking' : `${booking.cart} cart` },
            { label: 'Payment', value: `R${booking.total}` },
          ].map((item) => (
            <div key={`conf-${item.label}`}>
              <div className="text-muted-foreground text-xs mb-0.5">{item.label}</div>
              <div className="font-semibold text-foreground">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link href="/online-booking" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2 justify-center">
          <Icon name="CalendarDaysIcon" size={16} variant="outline" />
          Book Another Round
        </Link>
        <Link href="/member-portal" className="px-6 py-3 rounded-xl border-2 border-border text-foreground text-sm font-semibold hover:bg-muted transition-colors inline-flex items-center gap-2 justify-center">
          <Icon name="UserIcon" size={16} variant="outline" />
          View My Profile
        </Link>
      </div>
    </div>
  );
}